'use client';

import styles from './FleetBusiness.module.scss';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useRef, useState } from 'react';
import ContactUsForm from './Form/ContactUsForm';
import * as yup from 'yup';
import ThankYouModal from './ThankYouModal/ThankYouModal';
import { Image } from '@sitecore-jss/sitecore-jss-react';
import request from '../../../utils/request';
import { filterExactPostcodeMatches } from '../../../utils/postcode';
import { useLocation } from 'react-router-dom';
import { getJaecooSiteLang } from '../../../utils/cookie';
import { useSearchParams } from 'react-router-dom';

const VALIDATE_POSTCODE_API = '/api/sitecore/store/Validate';
const NEAREST_STORE_API = '/api/sitecore/store/SearchStore';

// CMS 的 retailerOption（store item 结构：storeName/storeCode/postalCode）映射为下拉框选项，作为初始全量列表
const mapCmsRetailerOptions = (items = []) =>
  items.map((item) => ({
    label: item.fields?.storeName?.value ?? item.displayName ?? '',
    value: item.fields?.storeCode?.value ?? '',
    postCode: item.fields?.postalCode?.value ?? '',
  }));

// 接口返回的扁平门店结构映射为下拉框选项
// TODO: 门店名称/邮编字段名以后端最终返回为准
const mapApiStores = (stores = []) =>
  stores.map((store) => ({
    label: store.storeName ?? store.name ?? store.storeCode,
    value: store.storeCode,
    postCode: store.postCode ?? store.postalCode ?? '',
  }));

export default function ContactForm({ fields, rendering }) {
  const location = useLocation();

  const [searchParams, setSearchParams] = useSearchParams();

  const pageDec = searchParams.get('description');

  // 获取当前语言
  const getCurrentLanguage = () => {
    const cookieLang = getJaecooSiteLang();
    if (cookieLang) return cookieLang;

    const path = location.pathname;
    if (path.startsWith('/en')) return 'en';
    if (path.startsWith('/nl')) return 'nl';
    if (path.startsWith('/be')) return 'be';
    return 'en';
  };

  const currentLang = getCurrentLanguage();

  // 荷兰本地手机号验证规则（英文版和荷兰版一致）
  const getPhoneValidation = () => {
    // 动态生成手机号验证规则
    return (value) => {
      if (value.startsWith('32')) {
        // 32开头，后面8-11位数字
        return /^32\d{8,11}$/.test(value);
      } else if (value.startsWith('352')) {
        // 352开头，后面8-11位数字
        return /^352\d{8,11}$/.test(value);
      } else {
        // 不是32开头，只要求是数字即可，没有长度要求
        return /^\d+$/.test(value);
      }
    };
  };

  const schema = yup.object({
    carModelCode: yup.string().required(fields.requiredMessage?.value),
    carVersion: yup.string(), // 可选字段
    carColor: yup.string(), // 可选字段
    interested: yup.boolean().oneOf([true, false]).required(fields.requiredMessage?.value),
    firstName: yup.string().required(fields.requiredMessage?.value),
    lastName: yup.string().required(fields.requiredMessage?.value),
    email: yup
      .string()
      .email(fields.emailVerification?.value)
      .required(fields.requiredMessage?.value),
    phoneNumber: yup
      .string()
      .required(fields.requiredMessage?.value)
      .test('valid-phone-number', fields.mobileNumberVerification?.value, getPhoneValidation()),
    // .matches(getPhoneValidation(), fields.mobileNumberVerification?.value),
    storeCode: yup.string().required(fields.requiredMessage?.value),
    feedback: yup.string(),
    termsAndConditionsCheck: yup.boolean().oneOf([true], fields.requiredMessage?.value).required(),
  });
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultType, setResultType] = useState(false);
  // CMS 的 retailerOption（store item 结构：storeName/storeCode/postalCode）映射为下拉框选项，作为初始全量经销商列表
  const [retailerOptions, setRetailerOptions] = useState(() =>
    mapCmsRetailerOptions(fields?.retailerOption)
  );
  // 缓存全量经销商列表，供邮编查询后恢复
  const allRetailersRef = useRef(retailerOptions);

  // 下拉框输入邮编时（防抖 2 秒后）触发查询接口
  const handleRetailerSearch = useCallback(
    async (keyword) => {
      const trimmed = (keyword || '').trim();

      // 输入清空：恢复缓存的全量列表
      if (!trimmed) {
        setRetailerOptions(allRetailersRef.current);
        return;
      }

      // 比利时邮编为 4 位，未满 4 位不触发查询（本地前缀排序照常工作）
      if (trimmed.length < 4) return;

      try {
        // 第一步：校验邮编合法性（与 FindDealer 流程一致）
        const validateRes = await request.post(VALIDATE_POSTCODE_API, {
          postcode: trimmed,
        });
        if (!(validateRes?.code === 200 && validateRes?.data)) {
          // 邮编不合法：置空选项，下拉框显示 searchEmpty 文案
          setRetailerOptions([]);
          return;
        }

        // 第二步：校验通过后查询经销商
        // TODO: root 待与后端确认（当前传 ContactForm 的 datasource id）
        const res = await request.post(NEAREST_STORE_API, {
          root: rendering?.dataSource,
          postcode: trimmed,
        });
        // 兼容两种返回结构：data 直接是数组，或 data.nearbyStores 是数组
        const stores = Array.isArray(res?.data) ? res.data : res?.data?.nearbyStores;
        if (res?.code === 200 && Array.isArray(stores)) {
          // 绝对匹配优先：有 postcode 完全匹配的只保留匹配项，否则显示接口返回的全部
          const filtered = filterExactPostcodeMatches(stores, trimmed);
          // 有结果则替换选项；空数组则置空，下拉框显示 searchEmpty 文案
          setRetailerOptions(mapApiStores(filtered));
        }
      } catch (error) {
        // 查询失败时保留当前列表，不打断用户输入
        console.error('按邮编查询经销商失败:', error);
      }
    },
    [rendering?.dataSource]
  );

  const methods = useForm({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });
  const onSubmit = async (data) => {
    if (isSubmitting) return; // 防止重复提交

    setIsSubmitting(true);

    try {
      // 删除  termsAndConditionsCheck  字段
      delete data.termsAndConditionsCheck;
      data.countryId = 'be';
      const urlParams = new URLSearchParams(window.location.search);
      data.secondarySource = urlParams.get('secondarySource');
      data.userid = urlParams.get('userid') ? urlParams.get('userid') : undefined;
      if (typeof data.Subcription === 'undefined') {
        data.Subcription = false;
      }

      if (pageDec) {
        data.secondarySourceDescription = pageDec;
      }

      const result = await request.post('/api/sitecore/cars/ContactUsSubmit', data);

      // console.log(result, 'submit success');
      setShowModal(true);
      if (result.code === 200) {
        setResultType(true);
      } else {
        setResultType(false);
      }
    } catch (error) {
      console.error('提交失败:', error);
      setShowModal(true);
      setResultType(false);
      // 可以在这里添加错误处理逻辑
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setResultType(false);
    methods.reset();
  };

  const [selectMdImage, setSelectMdImage] = useState(fields.carImage);

  const emitChangeSome = (selectedModelOption) => {
    if (fields?.carImageList?.length) {
      const hasSt = fields.carImageList.some((val) => {
        if (val.fields?.key?.value === selectedModelOption?.label) {
          setSelectMdImage(val.fields?.image);
          return true;
        }
      });

      if (!hasSt) {
        setSelectMdImage(fields.carImage);
      }
    }
  };

  return (
    <div className={'container'}>
      <div className={styles.wrapper}>
        <div className={styles.main}>
          <h2 className={styles.title}>{fields.formTitle?.value}</h2>
          <FormProvider {...methods}>
            <form noValidate onSubmit={methods.handleSubmit(onSubmit)} className={styles.form}>
              <ContactUsForm
                fields={fields}
                isSubmitting={isSubmitting}
                emitChangeSome={emitChangeSome}
                retailerOptions={retailerOptions}
                onRetailerSearch={handleRetailerSearch}
              />
            </form>
          </FormProvider>
        </div>
        <div className={styles.imageContainer}>
          <Image field={selectMdImage}></Image>
        </div>
        <div className={styles.imageContainerTablet}>
          <Image field={selectMdImage}></Image>
        </div>
        <ThankYouModal
          visible={showModal}
          onClose={handleCloseModal}
          fields={fields}
          result={resultType}
        />
      </div>
    </div>
  );
}
